import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Upload as UploadIcon, FileSpreadsheet, CheckCircle, Loader2, Download, Settings, Table, ArrowRight, Building2, AlertCircle } from 'lucide-react';
import UploadProgress from '../components/UploadProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Upload = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const [stockFile, setStockFile] = useState(null);
  const [salesFile, setSalesFile] = useState(null);
  const [salesMonth, setSalesMonth] = useState(new Date().getMonth() + 1);
  const [salesYear, setSalesYear] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState({ stock: false, sales: false });
  const [uploadProgress, setUploadProgress] = useState({ stock: 0, sales: 0 });
  const [dragOver, setDragOver] = useState({ stock: false, sales: false });
  
  // Depot selection for admin
  const [depots, setDepots] = useState([]);
  const [selectedDepot, setSelectedDepot] = useState('');
  
  // Fetch depots for admin
  useEffect(() => {
    if (isAdmin) {
      const fetchDepots = async () => {
        try {
          const response = await axios.get(`${API}/depots`, { headers: getAuthHeaders() });
          setDepots(response.data);
        } catch (error) {
          console.error('Error fetching depots:', error);
        }
      };
      fetchDepots();
    }
  }, [isAdmin, getAuthHeaders]);
  
  // Column mapping state for stock
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [columnMapping, setColumnMapping] = useState({
    headerLine: 1,
    itemNameColumn: 1,
    quantityColumn: 2,
    skuColumn: 0  // 0 means not used
  });
  
  // Column mapping state for sales
  const [showSalesMappingDialog, setShowSalesMappingDialog] = useState(false);
  const [salesPreviewData, setSalesPreviewData] = useState(null);
  const [loadingSalesPreview, setLoadingSalesPreview] = useState(false);
  const [salesColumnMapping, setSalesColumnMapping] = useState({
    headerLine: 1,
    itemNameColumn: 1,
    quantityColumn: 2,
    skuColumn: 0  // 0 means not used
  });

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [type]: false }));
    
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      if (type === 'stock') setStockFile(file);
      else setSalesFile(file);
    } else {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
    }
  }, []);

  const handleStockFileSelect = async (file) => {
    if (!file) return;
    setStockFile(file);
    
    // Auto-open mapping dialog for preview
    setLoadingPreview(true);
    setShowMappingDialog(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/stock/preview`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(response.data);
      
      // Reset to defaults - Item Name is primary, SKU is optional
      setColumnMapping({
        headerLine: 1,
        itemNameColumn: 1,
        quantityColumn: 2,
        skuColumn: 0  // 0 means not used
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to preview file';
      if (errorMsg.includes('zip') || errorMsg.includes('.xls')) {
        toast.error('Please save your file as .xlsx format (Excel Workbook). The older .xls format is not supported.');
      } else {
        toast.error(errorMsg);
      }
      setShowMappingDialog(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleUploadStock = async () => {
    if (!stockFile) {
      toast.error('Please select a stock file');
      return;
    }
    
    // Admin must select a depot
    if (isAdmin && !selectedDepot) {
      toast.error('Please select a depot to upload stock for');
      return;
    }

    setUploading(prev => ({ ...prev, stock: true }));
    setUploadProgress(prev => ({ ...prev, stock: 0 }));
    setShowMappingDialog(false);
    
    const formData = new FormData();
    formData.append('file', stockFile);

    try {
      const params = new URLSearchParams({
        header_line: columnMapping.headerLine,
        item_name_column: columnMapping.itemNameColumn,
        quantity_column: columnMapping.quantityColumn,
        sku_column: columnMapping.skuColumn
      });
      
      // Add depot_id for admin
      if (isAdmin && selectedDepot) {
        params.append('depot_id', selectedDepot);
      }
      
      const response = await axios.post(`${API}/stock/upload?${params.toString()}`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, stock: percentCompleted }));
        }
      });
      
      // Show detailed message
      const { items_processed, items_skipped, skip_details } = response.data;
      if (items_skipped > 0) {
        let skipMsg = `${items_processed} items uploaded, ${items_skipped} skipped`;
        if (skip_details) {
          const reasons = [];
          if (skip_details.empty_item_name > 0) reasons.push(`${skip_details.empty_item_name} empty Item Name`);
          if (skip_details.short_row > 0) reasons.push(`${skip_details.short_row} short rows`);
          if (skip_details.no_depot > 0) reasons.push(`${skip_details.no_depot} no depot`);
          if (reasons.length > 0) skipMsg += ` (${reasons.join(', ')})`;
        }
        toast.warning(skipMsg);
      } else {
        toast.success(response.data.message);
      }
      
      setStockFile(null);
      setPreviewData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload stock file');
    } finally {
      setUploading(prev => ({ ...prev, stock: false }));
      setUploadProgress(prev => ({ ...prev, stock: 0 }));
    }
  };

  const handleUploadSales = async () => {
    if (!salesFile) {
      toast.error('Please select a sales file');
      return;
    }
    
    // Admin must select a depot
    if (isAdmin && !selectedDepot) {
      toast.error('Please select a depot to upload sales for');
      return;
    }

    setUploading(prev => ({ ...prev, sales: true }));
    setUploadProgress(prev => ({ ...prev, sales: 0 }));
    setShowSalesMappingDialog(false);
    
    const formData = new FormData();
    formData.append('file', salesFile);

    try {
      const params = new URLSearchParams({
        month: salesMonth,
        year: salesYear,
        header_line: salesColumnMapping.headerLine,
        item_name_column: salesColumnMapping.itemNameColumn,
        quantity_column: salesColumnMapping.quantityColumn,
        sku_column: salesColumnMapping.skuColumn
      });
      
      // Add depot_id for admin
      if (isAdmin && selectedDepot) {
        params.append('depot_id', selectedDepot);
      }
      
      const response = await axios.post(
        `${API}/sales/upload?${params.toString()}`, 
        formData, 
        { 
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, sales: percentCompleted }));
          }
        }
      );
      
      const { items_processed, items_skipped, skip_details } = response.data;
      if (items_skipped > 0) {
        let skipMsg = `${items_processed} items uploaded, ${items_skipped} skipped`;
        if (skip_details) {
          const reasons = [];
          if (skip_details.empty_item_name > 0) reasons.push(`${skip_details.empty_item_name} empty Item Name`);
          if (skip_details.short_row > 0) reasons.push(`${skip_details.short_row} short rows`);
          if (skip_details.no_depot > 0) reasons.push(`${skip_details.no_depot} no depot`);
          if (reasons.length > 0) skipMsg += ` (${reasons.join(', ')})`;
        }
        toast.warning(skipMsg);
      } else {
        toast.success(response.data.message);
      }
      
      setSalesFile(null);
      setSalesPreviewData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload sales file');
    } finally {
      setUploading(prev => ({ ...prev, sales: false }));
      setUploadProgress(prev => ({ ...prev, sales: 0 }));
    }
  };
  
  const handleSalesFileSelect = async (file) => {
    if (!file) return;
    setSalesFile(file);
    
    // Auto-open mapping dialog for preview
    setLoadingSalesPreview(true);
    setShowSalesMappingDialog(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/sales/preview`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setSalesPreviewData(response.data);
      
      // Reset to defaults - Item Name is primary, SKU is optional
      setSalesColumnMapping({
        headerLine: 1,
        itemNameColumn: 1,
        quantityColumn: 2,
        skuColumn: 0  // 0 means not used
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to preview file';
      if (errorMsg.includes('zip') || errorMsg.includes('.xls')) {
        toast.error('Please save your file as .xlsx format (Excel Workbook). The older .xls format is not supported.');
      } else {
        toast.error(errorMsg);
      }
      setShowSalesMappingDialog(false);
    } finally {
      setLoadingSalesPreview(false);
    }
  };
  
  // Get preview of mapped sales data
  const getSalesMappedPreview = () => {
    if (!salesPreviewData) return [];
    const dataStartLine = salesColumnMapping.headerLine + 1;
    return salesPreviewData.preview_rows
      .filter(row => row.line >= dataStartLine)
      .slice(0, 5)
      .map(row => ({
        itemName: row.data[salesColumnMapping.itemNameColumn - 1] || '',
        quantity: row.data[salesColumnMapping.quantityColumn - 1] || '',
        sku: salesColumnMapping.skuColumn > 0 ? (row.data[salesColumnMapping.skuColumn - 1] || '') : ''
      }));
  };

  const downloadTemplate = (type) => {
    let data, filename;
    if (type === 'stock') {
      data = [
        ['Item Name', 'Quantity'],
        ['1 Way Switch', 100],
        ['2 Way Switch', 80],
        ['Socket', 120]
      ];
      filename = 'stock_template.xlsx';
    } else {
      data = [
        ['Item Name', 'Quantity Sold'],
        ['1 Way Switch', 50],
        ['2 Way Switch', 40],
        ['Socket', 60]
      ];
      filename = 'sales_template.xlsx';
    }
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, filename);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  // Get preview of mapped data
  const getMappedPreview = () => {
    if (!previewData) return [];
    const dataStartLine = columnMapping.headerLine + 1;
    return previewData.preview_rows
      .filter(row => row.line >= dataStartLine)
      .slice(0, 5)
      .map(row => ({
        itemName: row.data[columnMapping.itemNameColumn - 1] || '',
        quantity: row.data[columnMapping.quantityColumn - 1] || '',
        sku: columnMapping.skuColumn > 0 ? (row.data[columnMapping.skuColumn - 1] || '') : ''
      }));
  };

  return (
    <div className="space-y-6" data-testid="upload-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Upload Data
        </h1>
        <p className="text-slate-500 mt-1">Upload stock and sales data from Excel files</p>
      </div>

      {/* Admin Depot Selector */}
      {isAdmin && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Upload for Depot:</span>
              </div>
              <Select value={selectedDepot} onValueChange={setSelectedDepot}>
                <SelectTrigger className="w-[200px] bg-white" data-testid="admin-depot-select">
                  <SelectValue placeholder="Select a depot" />
                </SelectTrigger>
                <SelectContent>
                  {depots.map(depot => (
                    <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedDepot && (
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Please select a depot to upload data</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Show upload sections only when depot is selected (for admin) or always (for depot users) */}
      {(!isAdmin || selectedDepot) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stock Upload */}
          <Card data-testid="stock-upload-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileSpreadsheet className="w-5 h-5 text-blue-500" />
              Current FG Stock
            </CardTitle>
            <CardDescription>
              Upload your current finished goods stock levels with column mapping
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`upload-zone cursor-pointer ${dragOver.stock ? 'drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(prev => ({ ...prev, stock: true })); }}
              onDragLeave={() => setDragOver(prev => ({ ...prev, stock: false }))}
              onDrop={(e) => handleDrop(e, 'stock')}
              onClick={() => document.getElementById('stock-file').click()}
              data-testid="stock-dropzone"
            >
              <input
                id="stock-file"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => handleStockFileSelect(e.target.files[0])}
              />
              {stockFile ? (
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-medium text-slate-900">{stockFile.name}</p>
                    <p className="text-sm text-slate-500">{(stockFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              ) : (
                <>
                  <UploadIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium">Drop your Excel file here</p>
                  <p className="text-slate-400 text-sm mt-1">or click to browse</p>
                </>
              )}
            </div>

            {stockFile && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  handleStockFileSelect(stockFile);
                }}
                data-testid="configure-mapping-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configure Column Mapping
              </Button>
            )}

            {uploading.stock ? (
              <UploadProgress progress={uploadProgress.stock} message="Uploading stock data..." />
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={() => stockFile && previewData ? handleUploadStock() : handleStockFileSelect(stockFile)}
                  disabled={!stockFile || uploading.stock}
                  className="flex-1"
                  data-testid="upload-stock-btn"
                >
                  <UploadIcon className="w-4 h-4 mr-2" />
                  Upload Stock
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => downloadTemplate('stock')}
                  data-testid="download-stock-template"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Template
                </Button>
              </div>
            )}

            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-xs text-slate-600 font-medium mb-1">Required columns:</p>
              <p className="text-xs text-slate-500 font-mono">Item Name, Quantity</p>
              <p className="text-xs text-slate-400 mt-1">SKU is optional • Column mapping available for custom formats</p>
            </div>
          </CardContent>
        </Card>

        {/* Sales Upload */}
        <Card data-testid="sales-upload-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileSpreadsheet className="w-5 h-5 text-green-500" />
              Monthly Sales Data
            </CardTitle>
            <CardDescription>
              Upload item-wise sales for a specific month with column mapping
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month</Label>
                <Select value={salesMonth.toString()} onValueChange={(v) => setSalesMonth(parseInt(v))}>
                  <SelectTrigger data-testid="sales-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month, idx) => (
                      <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={salesYear.toString()} onValueChange={(v) => setSalesYear(parseInt(v))}>
                  <SelectTrigger data-testid="sales-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div
              className={`upload-zone cursor-pointer ${dragOver.sales ? 'drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(prev => ({ ...prev, sales: true })); }}
              onDragLeave={() => setDragOver(prev => ({ ...prev, sales: false }))}
              onDrop={(e) => handleDrop(e, 'sales')}
              onClick={() => document.getElementById('sales-file').click()}
              data-testid="sales-dropzone"
            >
              <input
                id="sales-file"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => handleSalesFileSelect(e.target.files[0])}
              />
              {salesFile ? (
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-medium text-slate-900">{salesFile.name}</p>
                    <p className="text-sm text-slate-500">{(salesFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              ) : (
                <>
                  <UploadIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium">Drop your Excel file here</p>
                  <p className="text-slate-400 text-sm mt-1">or click to browse</p>
                </>
              )}
            </div>

            {salesFile && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleSalesFileSelect(salesFile)}
                data-testid="configure-sales-mapping-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configure Column Mapping
              </Button>
            )}

            {uploading.sales ? (
              <UploadProgress progress={uploadProgress.sales} message="Uploading sales data..." />
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={() => salesFile && salesPreviewData ? handleUploadSales() : handleSalesFileSelect(salesFile)}
                  disabled={!salesFile || uploading.sales}
                  className="flex-1"
                  data-testid="upload-sales-btn"
                >
                  <UploadIcon className="w-4 h-4 mr-2" />
                  Upload Sales
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => downloadTemplate('sales')}
                  data-testid="download-sales-template"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Template
                </Button>
              </div>
            )}

            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-xs text-slate-600 font-medium mb-1">Required columns:</p>
              <p className="text-xs text-slate-500 font-mono">Item Name, Quantity Sold</p>
              <p className="text-xs text-slate-400 mt-1">SKU is optional • Column mapping available for custom formats</p>
            </div>
          </CardContent>
        </Card>
        </div>
      ) : (
        /* Show placeholder when no depot selected (admin only) */
        <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50">
          <CardContent className="py-16 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Select a Depot First</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Please select a depot from the dropdown above to upload Finished Goods stock or Sales data.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Column Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configure Column Mapping
            </DialogTitle>
          </DialogHeader>
          
          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading file preview...</span>
            </div>
          ) : previewData && (
            <div className="space-y-6">
              {/* File Info */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <FileSpreadsheet className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium">{previewData.filename}</p>
                  <p className="text-sm text-slate-500">
                    {previewData.total_rows} rows × {previewData.total_columns} columns
                  </p>
                </div>
              </div>

              {/* Mapping Configuration */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Header Line</Label>
                  <Select 
                    value={columnMapping.headerLine.toString()} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, headerLine: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="header-line-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: Math.min(previewData.preview_rows.length, 15) }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>Line {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">Data starts from line {columnMapping.headerLine + 1}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Item Name Column <span className="text-red-500">*</span></Label>
                  <Select 
                    value={columnMapping.itemNameColumn.toString()} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, itemNameColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="item-name-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.column_letters.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Quantity Column <span className="text-red-500">*</span></Label>
                  <Select 
                    value={columnMapping.quantityColumn.toString()} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, quantityColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="quantity-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData.column_letters.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>SKU Column <span className="text-slate-400 text-xs">(Optional)</span></Label>
                  <Select 
                    value={columnMapping.skuColumn.toString()} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, skuColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="sku-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Not Used</SelectItem>
                      {previewData.column_letters.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Raw Preview */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Table className="w-4 h-4" />
                  <Label>File Preview (First 15 rows)</Label>
                </div>
                <div className="border rounded-lg overflow-x-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-slate-600 w-16">Line</th>
                        {previewData.column_letters.map((letter, idx) => (
                          <th key={letter} className={`px-2 py-1.5 text-left font-medium min-w-[100px] ${
                            idx + 1 === columnMapping.itemNameColumn ? 'bg-green-100 text-green-700' :
                            idx + 1 === columnMapping.quantityColumn ? 'bg-purple-100 text-purple-700' :
                            idx + 1 === columnMapping.skuColumn && columnMapping.skuColumn > 0 ? 'bg-blue-100 text-blue-700' :
                            'text-slate-600'
                          }`}>
                            {letter}
                            {idx + 1 === columnMapping.itemNameColumn && <span className="ml-1">(Name)</span>}
                            {idx + 1 === columnMapping.quantityColumn && <span className="ml-1">(Qty)</span>}
                            {idx + 1 === columnMapping.skuColumn && columnMapping.skuColumn > 0 && <span className="ml-1">(SKU)</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.preview_rows.slice(0, 15).map((row) => (
                        <tr 
                          key={row.line} 
                          className={`border-t ${
                            row.line === columnMapping.headerLine ? 'bg-yellow-50 font-medium' : 
                            row.line > columnMapping.headerLine ? 'bg-white' : 'bg-slate-50 text-slate-400'
                          }`}
                        >
                          <td className="px-2 py-1.5 font-mono">
                            {row.line}
                            {row.line === columnMapping.headerLine && (
                              <Badge variant="secondary" className="ml-1 text-[10px] py-0">Header</Badge>
                            )}
                          </td>
                          {row.data.map((cell, idx) => (
                            <td key={idx} className={`px-2 py-1.5 truncate max-w-[150px] ${
                              idx + 1 === columnMapping.itemNameColumn ? 'bg-green-50' :
                              idx + 1 === columnMapping.quantityColumn ? 'bg-purple-50' :
                              idx + 1 === columnMapping.skuColumn && columnMapping.skuColumn > 0 ? 'bg-blue-50' : ''
                            }`}>
                              {cell || <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mapped Preview */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  <Label>Mapped Data Preview</Label>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Item Name</th>
                        <th className="px-3 py-2 text-right">Quantity</th>
                        {columnMapping.skuColumn > 0 && <th className="px-3 py-2 text-left">SKU</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {getMappedPreview().length > 0 ? (
                        getMappedPreview().map((row, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 font-medium">{row.itemName || '—'}</td>
                            <td className="px-3 py-2 text-right font-mono">{row.quantity || '—'}</td>
                            {columnMapping.skuColumn > 0 && <td className="px-3 py-2 font-mono text-sm text-slate-500">{row.sku || '—'}</td>}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columnMapping.skuColumn > 0 ? 3 : 2} className="px-3 py-4 text-center text-slate-400">
                            No data rows found. Check your header line setting.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500">
                  Showing first 5 data rows (starting from line {columnMapping.headerLine + 1})
                  {columnMapping.skuColumn === 0 && <span className="text-slate-400"> • SKU will be auto-generated from Item Name</span>}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowMappingDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadStock} disabled={uploading.stock} data-testid="confirm-upload-btn">
              {uploading.stock ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadIcon className="w-4 h-4 mr-2" />
                  Upload with Mapping
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales Column Mapping Dialog */}
      <Dialog open={showSalesMappingDialog} onOpenChange={setShowSalesMappingDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configure Sales Column Mapping
            </DialogTitle>
          </DialogHeader>
          
          {loadingSalesPreview ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading file preview...</span>
            </div>
          ) : salesPreviewData && (
            <div className="space-y-6">
              {/* File Info */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <FileSpreadsheet className="w-8 h-8 text-green-500" />
                <div>
                  <p className="font-medium">{salesPreviewData.filename}</p>
                  <p className="text-sm text-slate-500">
                    {salesPreviewData.total_rows} rows × {salesPreviewData.total_columns} columns
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-sm font-medium text-green-600">
                    {months[salesMonth - 1]} {salesYear}
                  </p>
                  <p className="text-xs text-slate-400">Selected period</p>
                </div>
              </div>

              {/* Mapping Configuration */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Header Line</Label>
                  <Select 
                    value={salesColumnMapping.headerLine.toString()} 
                    onValueChange={(v) => setSalesColumnMapping(prev => ({ ...prev, headerLine: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="sales-header-line-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: Math.min(salesPreviewData.preview_rows.length, 15) }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>Line {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">Data starts from line {salesColumnMapping.headerLine + 1}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Item Name Column <span className="text-red-500">*</span></Label>
                  <Select 
                    value={salesColumnMapping.itemNameColumn.toString()} 
                    onValueChange={(v) => setSalesColumnMapping(prev => ({ ...prev, itemNameColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="sales-item-name-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {salesPreviewData.column_letters.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Quantity Sold Column <span className="text-red-500">*</span></Label>
                  <Select 
                    value={salesColumnMapping.quantityColumn.toString()} 
                    onValueChange={(v) => setSalesColumnMapping(prev => ({ ...prev, quantityColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="sales-quantity-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {salesPreviewData.column_letters.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>SKU Column <span className="text-slate-400 text-xs">(Optional)</span></Label>
                  <Select 
                    value={salesColumnMapping.skuColumn.toString()} 
                    onValueChange={(v) => setSalesColumnMapping(prev => ({ ...prev, skuColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="sales-sku-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Not Used</SelectItem>
                      {salesPreviewData.column_letters.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Raw Preview */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Table className="w-4 h-4" />
                  <Label>File Preview (First 15 rows)</Label>
                </div>
                <div className="border rounded-lg overflow-x-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-slate-600 w-16">Line</th>
                        {salesPreviewData.column_letters.map((letter, idx) => (
                          <th key={letter} className={`px-2 py-1.5 text-left font-medium min-w-[100px] ${
                            idx + 1 === salesColumnMapping.itemNameColumn ? 'bg-green-100 text-green-700' :
                            idx + 1 === salesColumnMapping.quantityColumn ? 'bg-purple-100 text-purple-700' :
                            idx + 1 === salesColumnMapping.skuColumn && salesColumnMapping.skuColumn > 0 ? 'bg-blue-100 text-blue-700' :
                            'text-slate-600'
                          }`}>
                            {letter}
                            {idx + 1 === salesColumnMapping.itemNameColumn && <span className="ml-1">(Name)</span>}
                            {idx + 1 === salesColumnMapping.quantityColumn && <span className="ml-1">(Qty)</span>}
                            {idx + 1 === salesColumnMapping.skuColumn && salesColumnMapping.skuColumn > 0 && <span className="ml-1">(SKU)</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {salesPreviewData.preview_rows.slice(0, 15).map((row) => (
                        <tr 
                          key={row.line} 
                          className={`border-t ${
                            row.line === salesColumnMapping.headerLine ? 'bg-yellow-50 font-medium' : 
                            row.line > salesColumnMapping.headerLine ? 'bg-white' : 'bg-slate-50 text-slate-400'
                          }`}
                        >
                          <td className="px-2 py-1.5 font-mono">
                            {row.line}
                            {row.line === salesColumnMapping.headerLine && (
                              <Badge variant="secondary" className="ml-1 text-[10px] py-0">Header</Badge>
                            )}
                          </td>
                          {row.data.map((cell, idx) => (
                            <td key={idx} className={`px-2 py-1.5 truncate max-w-[150px] ${
                              idx + 1 === salesColumnMapping.itemNameColumn ? 'bg-green-50' :
                              idx + 1 === salesColumnMapping.quantityColumn ? 'bg-purple-50' :
                              idx + 1 === salesColumnMapping.skuColumn && salesColumnMapping.skuColumn > 0 ? 'bg-blue-50' : ''
                            }`}>
                              {cell || <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mapped Preview */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  <Label>Mapped Data Preview</Label>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Item Name</th>
                        <th className="px-3 py-2 text-right">Quantity Sold</th>
                        {salesColumnMapping.skuColumn > 0 && <th className="px-3 py-2 text-left">SKU</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {getSalesMappedPreview().length > 0 ? (
                        getSalesMappedPreview().map((row, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 font-medium">{row.itemName || '—'}</td>
                            <td className="px-3 py-2 text-right font-mono">{row.quantity || '—'}</td>
                            {salesColumnMapping.skuColumn > 0 && <td className="px-3 py-2 font-mono text-sm text-slate-500">{row.sku || '—'}</td>}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={salesColumnMapping.skuColumn > 0 ? 3 : 2} className="px-3 py-4 text-center text-slate-400">
                            No data rows found. Check your header line setting.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500">
                  Showing first 5 data rows (starting from line {salesColumnMapping.headerLine + 1})
                  {salesColumnMapping.skuColumn === 0 && <span className="text-slate-400"> • SKU will be auto-generated from Item Name</span>}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSalesMappingDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadSales} disabled={uploading.sales} data-testid="confirm-sales-upload-btn">
              {uploading.sales ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadIcon className="w-4 h-4 mr-2" />
                  Upload Sales Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Upload;
